import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITIES = [
  { value: 'low', label: 'Basse', color: 'bg-slate-100 text-slate-700' },
  { value: 'medium', label: 'Normale', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'Haute', color: 'bg-red-100 text-red-700' },
];

const STATUSES = [
  { value: 'todo', label: 'À faire', color: 'bg-slate-100 text-slate-700', icon: 'bi-circle' },
  { value: 'in_progress', label: 'En cours', color: 'bg-amber-100 text-amber-700', icon: 'bi-arrow-clockwise' },
  { value: 'done', label: 'Terminée', color: 'bg-emerald-100 text-emerald-700', icon: 'bi-check-circle-fill' },
];

const STATUS_FILTERS = [
  { value: 'todo', label: 'À faire' },
  { value: 'in_progress', label: 'En cours' },
  { value: 'done', label: 'Terminées' },
  { value: 'overdue', label: 'En retard' },
  { value: 'all', label: 'Toutes' },
];

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isOverdue = (t) => {
  if (!t.due_date || t.status === 'done') return false;
  return new Date(t.due_date) < new Date();
};

// ====== Modal de création / édition ======
const TaskModal = ({ task, users, onClose, onSaved }) => {
  const isEdit = !!task?.id;
  const [form, setForm] = useState({
    title: task?.title || '',
    description: task?.description || '',
    due_date: task?.due_date ? task.due_date.slice(0, 10) : '',
    priority: task?.priority || 'medium',
    status: task?.status || 'todo',
    assigned_to_user_ids: task?.assigned_to_user_ids || [],
    assignment_mode: (task?.assigned_to_user_ids?.length === 0) ? 'all' : 'specific',
  });
  const [submitting, setSubmitting] = useState(false);

  const toggleUser = (uid) => {
    setForm((f) => ({
      ...f,
      assigned_to_user_ids: f.assigned_to_user_ids.includes(uid)
        ? f.assigned_to_user_ids.filter((x) => x !== uid)
        : [...f.assigned_to_user_ids, uid],
    }));
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || null,
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        priority: form.priority,
        assigned_to_user_ids: form.assignment_mode === 'all' ? [] : form.assigned_to_user_ids,
      };
      if (isEdit) {
        payload.status = form.status;
        await axios.put(`${API}/tasks/${task.id}`, payload);
        toast.success('Tâche mise à jour');
      } else {
        await axios.post(`${API}/tasks`, payload);
        toast.success('Tâche créée');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="task-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <i className="bi bi-check2-square text-blue-600 text-lg"></i>
            </div>
            <h3 className="text-base font-bold text-slate-800">{isEdit ? 'Modifier la tâche' : 'Nouvelle tâche'}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="task-modal-close">
            <i className="bi bi-x-lg text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Titre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex : Relancer M. Dupont pour le mandat"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="task-form-title"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Échéance</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="task-form-due-date"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Priorité</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="task-form-priority"
              >
                {PRIORITIES.map((p) => (<option key={p.value} value={p.value}>{p.label}</option>))}
              </select>
            </div>
          </div>

          {isEdit && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Statut</label>
              <div className="grid grid-cols-3 gap-2">
                {STATUSES.map((s) => (
                  <button
                    type="button"
                    key={s.value}
                    onClick={() => setForm({ ...form, status: s.value })}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold border transition flex items-center justify-center gap-2 ${
                      form.status === s.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`task-form-status-${s.value}`}
                  >
                    <i className={`bi ${s.icon}`}></i>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2">Assignation</label>
            <div className="flex flex-col sm:flex-row gap-2 mb-2">
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 cursor-pointer hover:bg-slate-50">
                <input
                  type="radio"
                  name="assignment_mode"
                  checked={form.assignment_mode === 'all'}
                  onChange={() => setForm({ ...form, assignment_mode: 'all', assigned_to_user_ids: [] })}
                  data-testid="task-form-assign-all"
                />
                <span className="text-sm">Tous les utilisateurs de l'agence</span>
              </label>
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 cursor-pointer hover:bg-slate-50">
                <input
                  type="radio"
                  name="assignment_mode"
                  checked={form.assignment_mode === 'specific'}
                  onChange={() => setForm({ ...form, assignment_mode: 'specific' })}
                  data-testid="task-form-assign-specific"
                />
                <span className="text-sm">Utilisateurs spécifiques</span>
              </label>
            </div>
            {form.assignment_mode === 'specific' && (
              <div className="border border-slate-200 rounded-lg p-2 max-h-40 overflow-y-auto bg-slate-50">
                {users.length === 0 ? (
                  <p className="text-xs text-slate-500 text-center py-2">Aucun utilisateur disponible</p>
                ) : (
                  users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 py-1 px-2 hover:bg-white rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.assigned_to_user_ids.includes(u.id)}
                        onChange={() => toggleUser(u.id)}
                        data-testid={`task-form-user-${u.id}`}
                      />
                      <span className="text-sm">{u.prenom} {u.nom} <span className="text-xs text-slate-500">· {u.email}</span></span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Description (optionnel)</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Précisions, contexte..."
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
              data-testid="task-form-description"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg" data-testid="task-form-cancel">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !form.title.trim()}
            className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg flex items-center gap-2"
            data-testid="task-form-submit"
          >
            <i className="bi bi-check-lg"></i>
            {submitting ? 'Enregistrement…' : (isEdit ? 'Mettre à jour' : 'Créer la tâche')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ====== Page principale ======
const TasksPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('todo');
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // Ouverture auto du modal « Nouvelle tâche » via `?new=1`.
  // Utilisé depuis la bottom-sheet mobile « Créer » → /taches?new=1.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setEditingTask(null);
      setShowModal(true);
      // Nettoie l'URL pour éviter de rouvrir le modal après un F5.
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // lot DJ — arrivée depuis une notification : /taches?tache={id} ouvre la tâche
  useEffect(() => {
    const cible = searchParams.get('tache');
    if (!cible || !tasks.length) return;
    const tache = tasks.find((t) => t.id === cible);
    if (tache) {
      setEditingTask(tache);
      setShowModal(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('tache');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, tasks]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = (statusFilter === 'overdue' || statusFilter === 'all') ? {} : { status: statusFilter };
      const res = await axios.get(`${API}/tasks`, { params });
      setTasks(res.data || []);
    } catch (e) {
      toast.error('Impossible de charger les tâches');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/negotiators`);
      setUsers(res.data || []);
    } catch {
      setUsers([]);
    }
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filteredTasks = useMemo(() => {
    if (statusFilter === 'overdue') return tasks.filter(isOverdue);
    return tasks;
  }, [tasks, statusFilter]);

  const handleToggle = async (task) => {
    try {
      await axios.post(`${API}/tasks/${task.id}/complete`);
      loadTasks();
      window.dispatchEvent(new Event('tasks-changed'));
    } catch {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleSetStatus = async (task, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, { status: newStatus });
      loadTasks();
      window.dispatchEvent(new Event('tasks-changed'));
    } catch {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Supprimer la tâche "${task.title}" ?`)) return;
    try {
      await axios.delete(`${API}/tasks/${task.id}`);
      toast.success('Tâche supprimée');
      loadTasks();
      window.dispatchEvent(new Event('tasks-changed'));
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="p-3 md:p-6 space-y-4 bg-slate-50 min-h-screen" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <i className="bi bi-check2-square text-blue-600 text-xl sm:text-2xl"></i>
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Tâches</h1>
            <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Suivi des actions et relances</p>
          </div>
        </div>
        <button
          onClick={() => { setEditingTask(null); setShowModal(true); }}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition flex items-center gap-2"
          data-testid="task-add-btn"
        >
          <i className="bi bi-plus-circle-fill"></i>
          Nouvelle tâche
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                statusFilter === s.value ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`task-filter-${s.value}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500"><i className="bi bi-arrow-clockwise animate-spin mr-2"></i>Chargement…</div>
        ) : filteredTasks.length === 0 ? (
          <div className="p-8 text-center">
            <i className="bi bi-clipboard-check text-4xl text-slate-300 block mb-3"></i>
            <p className="text-sm text-slate-600 mb-4">Aucune tâche {statusFilter !== 'all' ? `(${STATUS_FILTERS.find(f => f.value === statusFilter)?.label.toLowerCase()})` : ''}</p>
            <button onClick={() => { setEditingTask(null); setShowModal(true); }} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">
              <i className="bi bi-plus-lg mr-1"></i>Créer une tâche
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filteredTasks.map((t) => {
              const overdue = isOverdue(t);
              const priority = PRIORITIES.find((p) => p.value === t.priority) || PRIORITIES[1];
              const statusObj = STATUSES.find((s) => s.value === t.status) || STATUSES[0];
              return (
                <li key={t.id} className={`p-4 hover:bg-slate-50 transition ${t.status === 'done' ? 'opacity-60' : ''}`} data-testid={`task-row-${t.id}`}>
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => handleToggle(t)}
                      className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition ${
                        t.status === 'done' ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 hover:border-emerald-500'
                      }`}
                      data-testid={`task-toggle-${t.id}`}
                      aria-label="Marquer terminée"
                      title={t.status === 'done' ? 'Rouvrir la tâche' : 'Marquer terminée'}
                    >
                      {t.status === 'done' && <i className="bi bi-check-lg text-xs"></i>}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-medium text-slate-800 ${t.status === 'done' ? 'line-through text-slate-500' : ''}`}>{t.title}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${priority.color}`}>{priority.label}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${statusObj.color}`} data-testid={`task-status-badge-${t.id}`}>
                          <i className={`bi ${statusObj.icon}`}></i>{statusObj.label}
                        </span>
                        {overdue && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-red-100 text-red-700">
                            <i className="bi bi-exclamation-triangle mr-1"></i>En retard
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-3">
                        {t.due_date && (
                          <span><i className="bi bi-calendar3 mr-1"></i>{formatDate(t.due_date)}</span>
                        )}
                        {t.assigned_to_user_ids?.length === 0 ? (
                          <span><i className="bi bi-people-fill mr-1"></i>Toute l'agence</span>
                        ) : (
                          <span><i className="bi bi-person-fill mr-1"></i>{t.assigned_to_user_ids.length} assigné(s)</span>
                        )}
                        {t.linked_property && (
                          <button
                            onClick={() => navigate(`/properties/${t.linked_property.id}/view`)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
                            data-testid={`task-linked-property-${t.id}`}
                          >
                            <i className="bi bi-shop"></i>
                            {t.linked_property.reference || t.linked_property.titre || 'Bien'}
                            {t.linked_property.ville && <span className="text-slate-400">· {t.linked_property.ville}</span>}
                          </button>
                        )}
                        {t.linked_contact && (
                          <button
                            onClick={() => navigate(`/contacts/${t.linked_contact.id}`)}
                            className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-700 hover:underline"
                            data-testid={`task-linked-contact-${t.id}`}
                          >
                            <i className="bi bi-person-circle"></i>
                            {`${t.linked_contact.prenom || ''} ${t.linked_contact.nom || ''}`.trim() || 'Contact'}
                          </button>
                        )}
                      </div>
                      {t.description && (
                        <p className="text-xs text-slate-600 mt-1.5 line-clamp-2 whitespace-pre-wrap">{t.description}</p>
                      )}
                      {/* Quick status actions */}
                      {t.status !== 'done' && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {t.status === 'todo' && (
                            <button
                              onClick={() => handleSetStatus(t, 'in_progress')}
                              className="text-[11px] px-2 py-1 rounded-md bg-amber-50 hover:bg-amber-100 text-amber-700 font-semibold inline-flex items-center gap-1"
                              data-testid={`task-set-inprogress-${t.id}`}
                            >
                              <i className="bi bi-arrow-clockwise"></i>Démarrer
                            </button>
                          )}
                          {t.status === 'in_progress' && (
                            <button
                              onClick={() => handleSetStatus(t, 'todo')}
                              className="text-[11px] px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold inline-flex items-center gap-1"
                              data-testid={`task-set-todo-${t.id}`}
                            >
                              <i className="bi bi-arrow-counterclockwise"></i>Mettre en attente
                            </button>
                          )}
                          <button
                            onClick={() => handleSetStatus(t, 'done')}
                            className="text-[11px] px-2 py-1 rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold inline-flex items-center gap-1"
                            data-testid={`task-set-done-${t.id}`}
                          >
                            <i className="bi bi-check-circle"></i>Marquer terminée
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => { setEditingTask(t); setShowModal(true); }} className="text-slate-400 hover:text-blue-500 p-1.5" title="Modifier" data-testid={`task-edit-${t.id}`}>
                        <i className="bi bi-pencil text-sm"></i>
                      </button>
                      <button onClick={() => handleDelete(t)} className="text-slate-400 hover:text-red-500 p-1.5" title="Supprimer" data-testid={`task-delete-${t.id}`}>
                        <i className="bi bi-trash text-sm"></i>
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showModal && (
        <TaskModal
          task={editingTask}
          users={users}
          onClose={() => { setShowModal(false); setEditingTask(null); }}
          onSaved={() => { loadTasks(); window.dispatchEvent(new Event('tasks-changed')); }}
        />
      )}
    </div>
  );
};

export default TasksPage;
// eslint-disable-next-line no-unused-vars
const _NAV = (navigate) => navigate('/taches');
